/* eslint-disable react/prop-types */

import React, { useEffect, useRef, useState } from 'react';
import * as echarts from 'echarts';
import 'bootstrap/dist/css/bootstrap.min.css';
import geoData from 'assets/json/MH_Districts_with_color_final.json';
import FalconComponentCard from 'components/common/FalconComponentCard';
import FalconCardHeader from 'components/common/FalconCardHeader';

// Add onDistrictClick prop to receive the district ID in parent component
// selectedDistrictId is passed from parent to ensure map stays in sync
const EChartsGeoJSONMap = ({ onDistrictClick, selectedDistrictId }) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  const [selectedDistrict, setSelectedDistrict] = useState(selectedDistrictId);

  // Define the disabled color - polygons with this color will be non-clickable
  const DISABLED_COLOR = '#EBEBEB';

  useEffect(() => {
    if (!chartRef.current) return;

    // Initialize the ECharts instance
    chartInstance.current = echarts.init(chartRef.current);

    // Prepare data for echarts with the colors from the GeoJSON
    const mapData = geoData.features.map(feature => {
      const originalColor = feature.properties.Color || '#FFFFFF';
      const isDisabled = originalColor.toUpperCase() === DISABLED_COLOR;

      return {
        name: feature.properties.District,
        value: 1, // Dummy value, needed for ECharts
        id: feature.properties.DISTRICT_L,
        originalColor: originalColor, // Store original color for resetting
        disabled: isDisabled, // Flag to check if this region is disabled
        // Use color from GeoJSON or white as default
        itemStyle: {
          areaColor:
            feature.properties.DISTRICT_L === selectedDistrictId
              ? '#ff6e67'
              : originalColor,
          borderColor: isDisabled ? '#ccc' : '#666', // Lighter border for disabled regions
          borderWidth: isDisabled ? 0.3 : 0.5
        }
      };
    });

    // Register the GeoJSON data
    echarts.registerMap('districts', geoData);

    // Chart options
    const option = {
      tooltip: {
        trigger: 'item',
        formatter: params => {
          // Don't show tooltip for disabled regions
          const district = mapData.find(item => item.name === params.name);
          if (district && district.disabled) {
            return '';
          }
          return params.name;
        }
      },
      series: [
        {
          name: 'Districts',
          type: 'map',
          map: 'districts',
          roam: true,
          scaleLimit: {
            min: 0.8, // Minimum zoom level (80% of normal size)
            max: 5 // Maximum zoom level (5x normal size)
          },
          nameProperty: 'District',
          // emphasis: {
          //   label: {
          //     show: params => {
          //       const district = mapData.find(
          //         item => item.name === params.name
          //       );
          //       return district && !district.disabled;
          //     },
          //     formatter: params => {
          //       const district = mapData.find(
          //         item => item.name === params.name
          //       );
          //       return district && !district.disabled ? params.name : '';
          //     }
          //   },
          //   itemStyle: {
          //     // This ensures that there's no color change on hover
          //     areaColor: params => {
          //       const district = mapData.find(
          //         item => item.name === params.name
          //       );
          //       if (district && district.disabled) {
          //         return DISABLED_COLOR;
          //       }
          //       if (district && district.id === selectedDistrictId) {
          //         return '#ff6e67';
          //       }
          //       return district ? district.originalColor : '#FFFFFF';
          //     }
          //   }
          // },
          select: {
            disabled: true
          },
          label: {
            show: params => {
              const name = typeof params === 'object' ? params.name : params;
              const district = mapData.find(item => item.name === name);
              return district && !district.disabled;
            },
            formatter: params => {
              const name = typeof params === 'object' ? params.name : params;
              const district = mapData.find(item => item.name === name);
              return district && !district.disabled ? name : '';
            },
            fontSize: 7
          },
          data: mapData
        }
      ]
    };

    // Set the chart options
    chartInstance.current.setOption(option);

    // Add click event handler
    chartInstance.current.on('click', params => {
      const districtData = mapData.find(item => item.name === params.name);

      // If region is disabled or no district found, do nothing
      if (!districtData || districtData.disabled) {
        return;
      }

      if (districtData && districtData.id) {
        // console.log('Clicked district ID:', districtData.id);

        // Update selected district
        setSelectedDistrict(districtData.id);

        // Highlight the selected district
        const updatedMapData = mapData.map(item => {
          if (item.id === districtData.id) {
            return {
              ...item,
              itemStyle: {
                ...item.itemStyle,
                areaColor: '#ff6e67' // Highlight color for selected district
              }
            };
          }
          return {
            ...item,
            itemStyle: {
              ...item.itemStyle,
              areaColor: item.originalColor // Reset other districts
            }
          };
        });

        chartInstance.current.setOption({
          series: [
            {
              data: updatedMapData
            }
          ]
        });

        // Send district ID to parent component via callback
        if (onDistrictClick && typeof onDistrictClick === 'function') {
          onDistrictClick(districtData.id);
        }
      }
    });

    // Resize handler
    const handleResize = () => {
      chartInstance.current && chartInstance.current.resize();
    };

    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      chartInstance.current && chartInstance.current.dispose();
    };
  }, [onDistrictClick, selectedDistrictId]);

  // Update map when selectedDistrictId changes from parent
  useEffect(() => {
    if (!chartInstance.current) return;

    if (selectedDistrictId !== selectedDistrict) {
      setSelectedDistrict(selectedDistrictId);

      // Get the current data from the chart
      const option = chartInstance.current.getOption();
      const currentMapData = option.series[0].data;

      // Update the map data to highlight the selected district or clear selection
      const updatedMapData = currentMapData.map(item => {
        // Don't change disabled regions
        if (item.disabled) {
          return item;
        }

        if (selectedDistrictId && item.id === selectedDistrictId) {
          return {
            ...item,
            itemStyle: {
              ...item.itemStyle,
              areaColor: '#ff6e67' // Highlight color for selected district
            }
          };
        } else {
          // Reset other districts to their original colors
          return {
            ...item,
            itemStyle: {
              ...item.itemStyle,
              areaColor: item.originalColor || '#FFFFFF'
            }
          };
        }
      });

      // Update the chart with the new data
      chartInstance.current.setOption({
        series: [
          {
            data: updatedMapData
          }
        ]
      });
    }
  }, [selectedDistrictId, selectedDistrict]);

  // Legend style
  const legendStyle = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '10px',
    marginTop: '10px'
  };

  const legendItemStyle = {
    display: 'flex',
    alignItems: 'center',
    marginRight: '20px'
  };

  const circleStyle = color => ({
    width: '16px',
    height: '16px',
    borderRadius: '50%',
    backgroundColor: color,
    marginRight: '8px',
    display: 'inline-block'
  });

  const selectedCircleStyle = {
    width: '16px',
    height: '16px',
    borderRadius: '50%',
    backgroundColor: '#ff6e67',
    marginRight: '8px',
    display: 'inline-block'
  };

  return (
    <FalconComponentCard className="h-100">
      <FalconCardHeader title="Maharashtra" />
      <FalconComponentCard.Body>
        <div ref={chartRef} style={{ height: '500px', width: '100%' }} />

        {/* Legend */}
        <div style={legendStyle}>
          <div style={legendItemStyle}>
            <span style={circleStyle('#94A8FF')}></span>
            <span>Reported</span>
          </div>
          <div style={legendItemStyle}>
            <span style={circleStyle('#b5c6ce')}></span>
            <span>Non-Reported</span>
          </div>
          <div style={legendItemStyle}>
            <span style={selectedCircleStyle}></span>
            <span>Selected District</span>
          </div>
        </div>
      </FalconComponentCard.Body>
    </FalconComponentCard>
  );
};

export default EChartsGeoJSONMap;
